/**
 * DriverPushActivationBanner
 * --------------------------
 * Prominent banner shown on the driver dashboard when Web Push notifications
 * are not yet enabled. Ensures a driver never misses an Express request
 * even when the browser tab is closed / the phone screen is off.
 *
 * The banner is:
 *   - Auto-hidden when push is already active or unsupported.
 *   - Session-dismissible (X button) → reappears on next session.
 *   - Attention-catching (yellow gradient + Bell icon + big CTA).
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { BellRing, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "trigo:driver-push-banner-dismissed";

export default function DriverPushActivationBanner() {
  const { user } = useAuth();
  const { supported, permission, subscribed, enable } = usePushNotifications(user?.id, user?.role);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Session-scoped dismissal so we nudge the driver again next time they log in.
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
  }, []);

  if (!user || user.role !== "driver") return null;
  if (!supported) return null;
  if (subscribed && permission === "granted") return null;
  if (dismissed) return null;

  const blocked = permission === "denied";

  const handleActivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (blocked) {
        toast.error("Débloquez les notifications dans les paramètres de votre navigateur pour ce site.");
        return;
      }
      const r = await enable();
      if (r.ok) {
        toast.success("Notifications activées 🔔", {
          description: "Vous recevrez les alertes Express même app fermée.",
        });
      } else if (r.reason === "denied") {
        toast.error("Permission refusée. Activez-les via les paramètres du navigateur.");
      } else {
        toast.error("Activation impossible. Réessayez sur un appareil compatible.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-[#FACC15] bg-gradient-to-br from-[#FEF9C3] via-[#FEF3C7] to-[#FDE68A] px-4 py-4 sm:px-5 shadow-sm"
      data-testid="driver-push-banner"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/60 hover:bg-white text-[#0A2540] flex items-center justify-center transition"
        aria-label="Fermer"
        data-testid="driver-push-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start sm:items-center gap-3 sm:gap-4 pr-8">
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#0A2540] flex items-center justify-center shrink-0 shadow-md">
          <BellRing className="w-5 h-5 sm:w-6 sm:h-6 text-[#FACC15]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-[#854D0E]">
            {blocked ? "Notifications bloquées" : "Ne manquez aucune course"}
          </p>
          <h3 className="text-sm sm:text-base font-black text-[#0A2540] leading-tight" style={{ fontFamily: "Outfit" }}>
            Activez les notifications Express
          </h3>
          <p className="text-[12px] sm:text-[13px] text-[#5B4A0F] leading-snug mt-0.5">
            {blocked
              ? "Vos notifications sont bloquées par votre navigateur. Réautorisez-les depuis les paramètres du site pour recevoir les courses Express même hors application."
              : "Recevez chaque demande Express instantanément, même quand votre navigateur ou l'application est fermé."}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={handleActivate}
              disabled={busy}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#0A2540] text-white text-sm font-bold hover:bg-[#0F2B46] transition disabled:opacity-50"
              data-testid="driver-push-banner-activate"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
              {busy ? "Activation…" : blocked ? "Comment débloquer" : "Activer maintenant"}
            </button>
            <span className="inline-flex items-center gap-1 text-[11px] text-[#0A2540]/70 font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> 100% gratuit, aucune donnée collectée
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
